import {useEffect, useRef} from 'react';
import {COLORS} from '../../../../../constants/colors';
import {IconSize} from '../../../../atoms/Icon/Icon';

export interface SponsoredCanvasProps {
  color: keyof typeof COLORS;
  icon: string;
  iconSize: keyof typeof IconSize;
  iconPosition: 'left' | 'right';
}

const fontSize = 10;
const fontFamily =
  '"ABC Whyte", "Helvetica Neue Condensed Bold", Arial, sans-serif';
const gap = 4;
const iconViewBox = 24;

// Preload the ABC Whyte font to ensure it's available for canvas rendering
// using a test element to trigger the font loading
const preloadFont = () => {
  const loadFont = () => {
    const testElement = document.createElement('span');
    testElement.style.fontFamily = fontFamily;
    testElement.style.fontSize = '0px';
    testElement.style.visibility = 'hidden';
    testElement.textContent = 'Font load test';
    document.body.appendChild(testElement);

    // Clean up the test element after a short delay
    setTimeout(() => {
      if (testElement.parentNode) {
        document.body.removeChild(testElement);
      }
    }, 50);
  };

  // Load the font only in the browser
  if (typeof document !== 'undefined') {
    loadFont();
  }
};

preloadFont();

const getDevicePixelRatio = (): number => {
  if (typeof window !== 'undefined') {
    return window.devicePixelRatio || 2;
  }

  return 2;
};

export const useSponsoredCanvas = ({
  skipRenderingText,
  text,
  color,
  icon,
  iconSize,
  iconPosition,
}: {
  text: string;
  skipRenderingText: boolean;
} & SponsoredCanvasProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;

    if (!canvas) {
      return;
    }

    const ctx = canvas.getContext('2d');

    if (!ctx) {
      return;
    }

    renderSponsoredContent({
      ctx,
      canvas,
      text,
      skipRenderingText,
      color,
      icon,
      iconSize,
      iconPosition,
    });
  }, [text, skipRenderingText, color, icon, iconPosition, iconSize]);

  return canvasRef;
};

const renderSponsoredContent = ({
  canvas,
  ctx,
  skipRenderingText,
  text,
  color,
  icon,
  iconSize,
  iconPosition,
}: {
  ctx: CanvasRenderingContext2D;
  canvas: HTMLCanvasElement;
  text: string;
  skipRenderingText: boolean;
} & SponsoredCanvasProps) => {
  ctx.clearRect(0, 0, canvas.width, canvas.height);

  // Set the font for text measurement
  ctx.font = `${fontSize}px ${fontFamily}`;

  const textWidth = ctx.measureText(text).width;
  const totalWidth = textWidth + gap + IconSize[iconSize];

  // Set the canvas dimensions accounting for device pixel ratio
  // This makes the canvas look sharp on high-DPI screens
  const displayWidth = totalWidth + 2;
  const displayHeight = IconSize[iconSize] + 2;

  canvas.style.width = `${displayWidth}px`;
  canvas.style.height = `${displayHeight}px`;

  const pixelRatio = getDevicePixelRatio();

  // Increase the actual pixel count to account for device pixel ratio
  canvas.width = Math.floor(displayWidth * pixelRatio);
  canvas.height = Math.floor(displayHeight * pixelRatio);

  // Set the font before scaling to ensure proper text rendering
  ctx.font = `${fontSize}px ${fontFamily}`;
  ctx.fillStyle = COLORS[color];
  ctx.textBaseline = 'middle';

  // Scale all drawing operations by the pixel ratio
  ctx.scale(pixelRatio, pixelRatio);

  if (skipRenderingText) {
    return;
  }

  // Calculate positions based on iconPosition
  let textX: number;
  let iconX: number;
  const iconY = displayHeight / 2 - IconSize[iconSize] / 2;

  if (iconPosition === 'left') {
    iconX = 0;
    textX = IconSize[iconSize] + gap;
  } else {
    textX = 0;
    iconX = textWidth + gap;
  }

  // Draw the text
  ctx.fillText(text, textX, displayHeight / 2);

  // Draw the icon
  const img = new Image();
  img.onload = () => {
    // Create a temporary canvas for the icon
    const tempCanvas = document.createElement('canvas');
    const tempCtx = tempCanvas.getContext('2d');

    if (!tempCtx) {
      return;
    }

    tempCanvas.width = iconViewBox;
    tempCanvas.height = iconViewBox;

    // Draw the icon on the temporary canvas
    tempCtx.drawImage(img, 0, 0, iconViewBox, iconViewBox);

    // Change svg color to the color of the text
    tempCtx.globalCompositeOperation = 'source-in';
    tempCtx.fillStyle = COLORS[color];
    tempCtx.fillRect(0, 0, iconViewBox, iconViewBox);

    // Now draw the colored icon to the main canvas
    ctx.save();

    // Scale down the icon to fit the size
    const scale = IconSize[iconSize] / iconViewBox;
    ctx.translate(iconX, iconY);
    ctx.scale(scale, scale);

    ctx.drawImage(tempCanvas, 0, 0);

    ctx.restore();
  };

  img.src = icon;
};
